"use client";

import { useState, useEffect, useRef } from "react";
import useWindowSize from "@/hooks/useWindowSize";
import ProjectCard from "./ProjectCard";
import SeeAllProjectsCard from "./SeeAllProjectCard";
import SectionHeader from "@/components/shared/headers/SectionHeader";
import { ProjectProps } from "@/types/project/project.type";
import { useDict } from "@/components/providers/LocaleProvider";

export default function Projects({
  projects,
}: {
  projects: ProjectProps[] | undefined;
}) {
  const [activeProject, setActiveProject] = useState(0);
  const { width } = useWindowSize();
  const t = useDict();
  const isMobileOrTablet = !!(width && width < 1024);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  // Coerce to an array so the rest of the component can never blow up
  // on `undefined` (e.g. when the DB call upstream returns an empty/
  // error shape).
  const safeProjects = projects ?? [];

  // 🔁 Auto-switch active project (desktop only)
  useEffect(() => {
    if (!isMobileOrTablet && safeProjects.length > 0) {
      intervalRef.current = setInterval(() => {
        setActiveProject((prev) => (prev + 1) % safeProjects.length);
      }, 6000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isMobileOrTablet, safeProjects.length]);

  const handleCardHover = (index: number) => {
    if (!isMobileOrTablet) setActiveProject(index);
  };

  return (
    <section className="relative overflow-hidden">
      <div>
        <SectionHeader
          title={t.home.projects.title}
          description={t.home.projects.description}
        />

        <div className="lg:h-[450px] flex flex-col lg:flex-row gap-6 mt-10 relative z-10">
          {safeProjects.map((project, index) => (
            <ProjectCard
              key={project.id}
              index={index}
              data={project}
              isActive={activeProject === index}
              images={[
                ...(project.images?.mockup ?? []),
                ...(project.images?.small ?? []),
              ]}
              onHover={handleCardHover}
              onClick={setActiveProject}
              isMobileOrTablet={isMobileOrTablet}
              totalProjects={safeProjects.length}
            />
          ))}
          <SeeAllProjectsCard />
        </div>
      </div>

      {/* Decorative blurred background for aesthetic touch
      <div className="absolute -top-32 right-0 w-[400px] h-[400px] bg-gradient-to-br from-primary/30 to-tertiary/20 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-[250px] h-[250px] bg-gradient-to-tr from-secondary/20 to-primary/10 blur-[100px] rounded-full pointer-events-none" /> */}
    </section>
  );
}
